import React, { useCallback, memo } from 'react';
import { type MAccordionHeaderProps, MAccordionHeaderAlignment } from './MAccordionHeader.types';
import * as Styled from './MAccordionHeader.styles';
import { AHeader } from '../../atoms/AHeader/index';
import { type AHeaderProps, AHeaderSize, AHeaderTag } from '../../atoms/AHeader/AHeader.types';
import { ALinkStandalone } from '../../atoms/ALinkStandalone';
import { type ALinkStandaloneProps, ALinkStandaloneSize } from '../../atoms/ALinkStandalone/ALinkStandalone.types';
import { useAccordionContext } from '../../organisms/OAccordion';
import clsx from 'clsx';

// // TODO: Get I18n keys for these hardcoded tokens
const TEXT_I18N_EXPAND_ALL = 'Expand All';
const TEXT_I18N_COLLAPSE_ALL = 'Collapse All';

const COMPONENT_NAME = 'm-web-ui-kit-MAccordionHeader';
const TEST_ID = 'web-ui-kit-MAccordionHeader';

const MAccordionHeader = memo(function MAccordionHeader({
  aHeader = {},
  aLinkStandalone = {},
  alignment = MAccordionHeaderAlignment.Left,
  link = false,
  ...rest
}: MAccordionHeaderProps) {
  const { itemIds, expandedSet, setExpandedIds } = useAccordionContext();

  const allExpanded = itemIds.length > 0 && itemIds.every(id => expandedSet.has(id));

  const onToggleAll = useCallback(() => {
    if (allExpanded) setExpandedIds([]);
    else setExpandedIds([...itemIds]);
  }, [allExpanded, itemIds, setExpandedIds]);

  const toggleText = allExpanded ? TEXT_I18N_COLLAPSE_ALL : TEXT_I18N_EXPAND_ALL;

  const headerProps = {
    element: AHeaderTag.h2,
    size: AHeaderSize.TitleS,
    ...aHeader,
    className: clsx(aHeader.className, 'm-accordion-header-title'),
  } as AHeaderProps;

  const linkProps = {
    size: ALinkStandaloneSize.Medium,
    ...aLinkStandalone,
    text: toggleText,
    'aria-label': toggleText,
    onClick: onToggleAll,
    href: undefined,
    className: clsx(aLinkStandalone.className, 'm-accordion-header-toggle'),
  } as ALinkStandaloneProps;

  return (
    <Styled.Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} alignment={alignment} {...rest}>
      {aHeader.text && <AHeader {...headerProps} />}
      {link && <ALinkStandalone {...linkProps} />}
    </Styled.Wrapper>
  );
});

export { MAccordionHeader, MAccordionHeaderAlignment, TEST_ID as M_ACCORDION_HEADER_TEST_ID };
export type { MAccordionHeaderProps };
